'use client';

import Link from 'next/link';

import { Button, Typography } from 'lighthouse';

import { SafeImage } from '@/widgets/SafeImage';
import { RatingStarIcon } from '@/widgets/StarRating';

import { GenerateFilePreviewUrl } from '@/utilities/GenerateFilePreviewUrl';

import { HOME_STORE_FULFILLMENT_PRESENTATION } from '../../constants';
import { StoreCardProps } from './models';
import { formatDistance, getDistanceValue, getRateValue } from './utils';

import { ArrowLeft2, Location, Map1, TickCircle } from 'iconsax-react';

const COVER_FALLBACK = '/images/vendor/vendor_default_cover_image.png';

const ICON_BUTTON_CLASS =
    'flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-solid border-N200 bg-white text-secondary transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary motion-reduce:transition-none';

export const StoreCard = ({ store, showDistance, isActive = false, onShowOnMap }: StoreCardProps) => {
    const rateValue = getRateValue(store.rate);
    const distanceValue = showDistance ? getDistanceValue(store.distanceKm) : null;
    const hasLocation = Number.isFinite(store.latitude) && Number.isFinite(store.longitude);
    const reviewsCount = store.ratesNumber || 0;
    const cityName = store.cityName?.trim();
    const inCoverage = showDistance && store.coverageStatus === 'IN_RANGE';
    const fulfillmentMethods = (store.fulfillmentTypes ?? []).filter((type) => HOME_STORE_FULFILLMENT_PRESENTATION[type]);
    const storeHref = `/vendor/${store.uuid}`;

    return (
        <article
            className={`group flex h-full flex-col overflow-hidden rounded-2xl border border-solid bg-white shadow-sm transition-all duration-200
                focus-within:border-primary focus-within:shadow-md motion-reduce:transition-none md:hover:-translate-y-0.5 md:hover:shadow-lg ${
                    isActive ? 'border-primary ring-2 ring-primary/20' : 'border-N200'
                }`}>
            <Link
                href={storeHref}
                tabIndex={-1}
                aria-hidden
                className='relative block h-28 w-full shrink-0 overflow-hidden bg-N50 no-underline md:h-32'>
                <SafeImage
                    fill
                    sizes='(min-width: 1158px) 33vw, (min-width: 783px) 50vw, 100vw'
                    src={
                        GenerateFilePreviewUrl({
                            attachableId: store.uuid,
                            attachment: 'STORE',
                            type: 'COVER',
                            isPrimary: true,
                            sortOrder: 0
                        }).src
                    }
                    alt=''
                    fallback={COVER_FALLBACK}
                    className='object-cover transition-transform duration-500 group-hover:scale-105 motion-reduce:transition-none'
                />
                <div aria-hidden className='pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent' />
                {rateValue !== null ? (
                    <span
                        role='img'
                        aria-label={`امتیاز ${rateValue.toLocaleString('fa-IR')} از ۵${reviewsCount ? `، بر اساس ${reviewsCount.toLocaleString('fa-IR')} نظر` : ''}`}
                        className='absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-1 shadow-sm
                            backdrop-blur-sm'>
                        <span aria-hidden className='inline-flex items-center gap-1.5'>
                            <RatingStarIcon size='sm' />
                            <Typography variant='body2' component='span' color='blackHighEmphasis' className='font-bold leading-5'>
                                {rateValue.toLocaleString('fa-IR')}
                            </Typography>
                            {!!reviewsCount && (
                                <Typography variant='caption' component='span' color='blackMediumEmphasis' className='leading-5'>
                                    ({reviewsCount.toLocaleString('fa-IR')})
                                </Typography>
                            )}
                        </span>
                    </span>
                ) : (
                    <span className='absolute left-3 top-3 rounded-full bg-secondary px-2.5 py-1 shadow-sm'>
                        <Typography variant='caption' component='span' className='font-bold leading-5 !text-white'>
                            فروشگاه جدید
                        </Typography>
                    </span>
                )}
            </Link>

            <div className='flex flex-1 flex-col gap-3 px-4 pb-4'>
                <div className='-mt-8 flex items-end gap-3'>
                    <span className='relative h-16 w-16 shrink-0 overflow-hidden rounded-2xl border-2 border-solid border-white bg-white shadow-md'>
                        <SafeImage
                            fill
                            sizes='64px'
                            src={
                                GenerateFilePreviewUrl({
                                    attachableId: store.uuid,
                                    attachment: 'STORE',
                                    type: 'LOGO',
                                    isPrimary: true,
                                    sortOrder: 0
                                }).src
                            }
                            alt={`لوگوی ${store.name}`}
                            fallback='/images/vendor/vendor_profile_default.png'
                            className='object-contain'
                        />
                    </span>
                    {(cityName || distanceValue !== null) && (
                        <div className='flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1 pb-1 text-blackMediumEmphasis'>
                            {cityName && (
                                <span className='inline-flex min-w-0 items-center gap-1' title={`شهر فروشگاه: ${cityName}`}>
                                    <Location size={14} color='currentColor' className='shrink-0' aria-hidden />
                                    <Typography variant='caption' component='span' color='blackMediumEmphasis' className='min-w-0 truncate leading-5'>
                                        <span className='sr-only'>شهر فروشگاه: </span>
                                        {cityName}
                                    </Typography>
                                </span>
                            )}
                            {distanceValue !== null && (
                                <Typography variant='caption' component='span' color='primary' className='font-bold leading-5'>
                                    {formatDistance(distanceValue)} تا تو
                                </Typography>
                            )}
                        </div>
                    )}
                </div>

                <div className='flex flex-col gap-1.5'>
                    <Typography variant='subtitle1' component='h3' color='blackHighEmphasis' className='!m-0 font-bold leading-7'>
                        <Link
                            href={storeHref}
                            className='line-clamp-1 rounded no-underline transition-colors hover:text-primary focus-visible:outline
                                focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary'>
                            {store.name}
                        </Link>
                    </Typography>
                    {store.shortDescription && (
                        <Typography variant='body2' component='p' color='blackMediumEmphasis' className='!m-0 line-clamp-2 min-h-12 leading-6'>
                            {store.shortDescription}
                        </Typography>
                    )}
                </div>

                {!!fulfillmentMethods.length && (
                    <ul aria-label='نوع خدمت' className='m-0 flex list-none flex-wrap items-center gap-x-3 gap-y-1 p-0 text-blackMediumEmphasis'>
                        {fulfillmentMethods.map((type) => {
                            const { title, Icon } = HOME_STORE_FULFILLMENT_PRESENTATION[type];

                            return (
                                <li key={type} className='flex items-center gap-1'>
                                    <Icon size={14} color='currentColor' className='shrink-0' aria-hidden />
                                    <Typography variant='caption' component='span' color='blackMediumEmphasis' className='leading-5'>
                                        {title}
                                    </Typography>
                                </li>
                            );
                        })}
                    </ul>
                )}
                {inCoverage && (
                    <span className='inline-flex items-center gap-1 text-success'>
                        <TickCircle size={14} color='currentColor' variant='Bold' className='shrink-0' aria-hidden />
                        <Typography variant='caption' component='span' className='text-current'>
                            موقعیت تو در محدودهٔ پوشش این فروشگاه است
                        </Typography>
                    </span>
                )}

                <div className='mt-auto flex items-center gap-2 border-0 border-t border-solid border-N100 pt-3'>
                    <Button
                        component={Link}
                        href={storeHref}
                        variant='outlined'
                        size='small'
                        fullWidth
                        endIcon={<ArrowLeft2 size={16} color='currentColor' aria-hidden />}
                        aria-label={`مشاهده فروشگاه ${store.name}`}
                        className='min-h-10 justify-center whitespace-nowrap'>
                        مشاهده فروشگاه
                    </Button>
                    {hasLocation && onShowOnMap && (
                        <button
                            type='button'
                            title='نمایش روی نقشه'
                            aria-label={`نمایش ${store.name} روی نقشه`}
                            onClick={() => onShowOnMap(store)}
                            className={ICON_BUTTON_CLASS}>
                            <Map1 size={18} color='currentColor' aria-hidden />
                        </button>
                    )}
                </div>
            </div>
        </article>
    );
};
